import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { adminStatusGuard } from './core/guards/admin-status-guard/admin-status-guard';
import { FunnelStatusGuard } from './core/guards/funnel-status-guard/funnel-status-guard';
import { CompleteComponent } from './pages/complete/complete.component';
import { DoNotContactComponent } from './pages/do-not-contact/do-not-contact.component';
import { LayoutComponent } from './pages/layout/layout.component';
import { MailInProcessComponent } from './pages/mail-in-process/mail-in-process.component';
import { MailingIssueComponent } from './pages/mailing-issue/mailing-issue.component';
import { ReadyForTabletComponent } from './pages/ready-for-tablet/ready-for-tablet.component';
import { ReapplyComponent } from './pages/reapply/reapply.component';
import { SetupPasswordComponent } from './pages/setup-password/setup-password.component';
import { ResetPasswordComponent } from './pages/signup/reset-password/reset-password.component';
import { UserVerificationComponent } from './pages/signup/user-verification/user-verification.component';
import { WaitlistDemandComponent } from './pages/waitlist-demand/waitlist-demand.component';
import { WaitlistOtherComponent } from './pages/waitlist-other/waitlist-other.component';
import { WaitlistRidesComponent } from './pages/waitlist-rides/waitlist-rides.component';
import { DriverInitiativeComponent } from './pages/driver-initiative/driver-initiative-response.component';
import { DriverTIAResponseComponent } from './pages/driver-tia/driver-tia-response.component';
import { DriverAppRedirectComponent } from './pages/driver-app-redirect/driver-app-redirect.component';
import { NotInterestedComponent } from './pages/not-interested/not-interested.component';
import { UnsubscribeComponent } from './pages/unsubscribe/unsubscribe.component';
import { AuthorizeComponent as UberAuthorizeComponent } from './pages/uber/authorize/authorize.component';
import { UberComponent } from './pages/my-account/uber/uber.component';
import { UberConnectRequiredComponent } from "./pages/uber-connect-required/uber-connect-required.component";
import { LeaderboardComponent } from './pages/leaderboard/leaderboard.component';
import { IncompleteAndContactSupportComponent } from './pages/incomplete-and-contact-support/incomplete-and-contact-support.component';
import { SetPhoneComponent } from './pages/set-phone/set-phone.component';
import { OrderTrackingComponent } from './pages/order-tracking/order-tracking.component';

const routes: Routes = [
    {
        path: 'response/make_tia/:token',
        component: DriverTIAResponseComponent,
    },
    {
        path: 'response/:initiative/:token/:response',
        component: DriverInitiativeComponent,
    },
    {
        path: 'driver/unsubscribe',
        component: UnsubscribeComponent,
    },
    {
        path: 'login/two_factor_code',
        pathMatch: 'full',
        component: UserVerificationComponent,
        canActivate: [FunnelStatusGuard],
    },
    {
        path: 'login',
        loadChildren: () =>
            import('./pages/signup/signup.module').then((m) => m.SignupModule),
        canActivate: [FunnelStatusGuard],
    },
    {
        path: 'dashboard',
        loadChildren: () =>
            import('./pages/dashboard/dashboard.module').then(
                (m) => m.DashboardModule
            ),
    },
    {
        path: 'my-account',
        loadChildren: () =>
            import('./pages/my-account/my-account.module').then(
                (m) => m.MyAccountModule
            ),
    },
    {
        path: 'reset',
        component: ResetPasswordComponent,
        canActivate: [FunnelStatusGuard],
    },
    {
        path: 'reapply',
        component: ReapplyComponent,
        canActivate: [FunnelStatusGuard],
    },
    {
        path: 'reset/:token',
        component: ResetPasswordComponent,
        canActivate: [FunnelStatusGuard],
    },
    {
        path: 'join',
        loadChildren: () =>
            import('./pages/join/join.module').then((m) => m.JoinModule),
        canActivate: [FunnelStatusGuard],
    },
    {
        path: 'join/referred_by/:referralCode',
        loadChildren: () =>
            import('./pages/join/join.module').then((m) => m.JoinModule),
        canActivate: [FunnelStatusGuard],
    },
    {
        path: 'info',
        loadChildren: () =>
            import('./pages/info/info.module').then((m) => m.InfoModule),
        canActivate: [FunnelStatusGuard],
    },
    {
        path: 'set_password',
        component: SetupPasswordComponent,
    },
    {
        path: 'set_phone',
        component: SetPhoneComponent,
    },
    {
        path: 'set_phone/:token',
        component: SetPhoneComponent,
    },
    {
        path: 'phone-reset/:token',
        component: SetPhoneComponent,
    },
    {
        path: 'waitlist-other',
        component: WaitlistOtherComponent,
        canActivate: [FunnelStatusGuard],
    },
    {
        path: 'waitlist_demand',
        component: WaitlistDemandComponent,
        canActivate: [FunnelStatusGuard],
    },
    {
        path: 'waitlist',
        component: WaitlistRidesComponent,
        canActivate: [FunnelStatusGuard],
    },
    {
        path: 'complete',
        component: CompleteComponent,
        canActivate: [FunnelStatusGuard],
    },
    {
        path: 'ready_for_tablet',
        component: ReadyForTabletComponent,
        canActivate: [FunnelStatusGuard],
    },
    {
        path: 'not_interested',
        component: NotInterestedComponent,
        canActivate: [FunnelStatusGuard],
    },
    {
        path: 'do-not-contact',
        component: DoNotContactComponent,
        canActivate: [FunnelStatusGuard],
    },
    {
        path: 'mailing_issue',
        component: MailingIssueComponent,
        canActivate: [FunnelStatusGuard],
    },
    {
        path: 'mail_in_process',
        component: MailInProcessComponent,
        canActivate: [FunnelStatusGuard],
    },
    {
        path: 'proof_of_rides',
        loadChildren: () =>
            import('./pages/proof-of-rides/proof-of-rides.module').then(
                (m) => m.ProofOfRidesModule
            ),
        canActivate: [FunnelStatusGuard],
    },
    {
        path: 'payment',
        loadChildren: () =>
            import('./pages/payment/payment.module').then(
                (m) => m.PaymentModule
            ),
        canActivate: [FunnelStatusGuard],
    },
    {
        path: 'profile',
        loadChildren: () =>
            import('./pages/profile/profile.module').then(
                (m) => m.ProfileModule
            ),
        canActivate: [FunnelStatusGuard],
    },
    {
        path: 'driver-app-redirect',
        component: DriverAppRedirectComponent,
    },
    {
        path: 'uber/authorize',
        component: UberAuthorizeComponent,
    },
    {
        path: 'uber/link-uber',
        component: UberComponent,
        canActivate: [FunnelStatusGuard],
    },
    {
        path: 'uber-connect-required',
        component: UberConnectRequiredComponent,
        canActivate: [FunnelStatusGuard],
    },
    {
        path: '',
        component: LayoutComponent,
        canActivate: [adminStatusGuard],
    },
    {
        path: 'leaderboard',
        component: LeaderboardComponent,
    },
    {
        path: 'incomplete-and-contact-support',
        component: IncompleteAndContactSupportComponent,
        canActivate: [FunnelStatusGuard],
    },
    {
        path: 'orders/tracking/:token',
        component: OrderTrackingComponent,
    },
    {
        path: '**',
        redirectTo: '/login',
    },
];

@NgModule({
    imports: [
        RouterModule.forRoot(routes, { scrollPositionRestoration: 'enabled' }),
    ],
    exports: [RouterModule],
})
export class AppRoutingModule {}
